import { onMounted, onBeforeUnmount } from 'vue';
import MessageApi from 'dashboard/api/inbox/message';

// Read receipts for private notes. A note counts as seen only when it was
// actually rendered on this agent's screen while the tab was visible — so the
// "who read it" list never claims someone read a note they never had in front
// of them. Reports are batched: one request per conversation per burst.
const reported = new Set();
const pending = new Map();
let flushTimer = null;

const flush = () => {
  flushTimer = null;
  pending.forEach((messageIds, conversationId) => {
    MessageApi.markNotesSeen(conversationId, Array.from(messageIds)).catch(
      () => {
        // A missed receipt is not worth surfacing to the agent
      }
    );
  });
  pending.clear();
};

const queueSeen = (conversationId, messageId) => {
  if (!conversationId || !messageId || reported.has(messageId)) return;
  reported.add(messageId);
  if (!pending.has(conversationId)) pending.set(conversationId, new Set());
  pending.get(conversationId).add(messageId);
  if (!flushTimer) flushTimer = setTimeout(flush, 1500);
};

export function useNoteReadReporter(elementRef, { messageId, conversationId }) {
  let observer = null;
  let waitingForVisibility = false;

  function onVisibilityChange() {
    if (document.visibilityState !== 'visible' || !waitingForVisibility) return;
    waitingForVisibility = false;
    // Re-observing replays the intersection check for the current viewport.
    observer?.unobserve(elementRef.value);
    if (elementRef.value) observer?.observe(elementRef.value);
  }

  const stop = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    observer?.disconnect();
    observer = null;
  };

  const handleEntries = entries => {
    if (!entries.some(entry => entry.isIntersecting)) return;
    // The tab can render a note in the background; that is not "read".
    if (document.visibilityState !== 'visible') {
      waitingForVisibility = true;
      return;
    }
    queueSeen(conversationId, messageId);
    stop();
  };

  onMounted(() => {
    if (!elementRef.value) return;
    observer = new IntersectionObserver(handleEntries, { threshold: 0 });
    observer.observe(elementRef.value);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onBeforeUnmount(stop);
}
